import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
// Functions
import { WEB_SERVICE_MIMS, protocols } from "../../../../config/constants";
import { getProtocolTitle, isProtocolWithCompensationControls } from "../../../../config/validateProtocols";
// Components
import {
  Container,
  Header,
  Title,
  ControlText,
  ControlsDiv,
  ProtocolControlHeaderDiv,
  ProtocolControlDiv,
  ProtocolControlAlignerDiv,
  RangeAlignerDiv,
  RangeSlider,
  ActionButtonsDiv,
  ActionButton,
} from "./styles";
import UseFetch from "../../../../config/useFetch";
import ToggleSwitch from "../../../toggleSwitch";
import ProtocolLevels from "./sections/protocolLevels";

const ProtocolControls = ({
  patientId,
  protocolId,
  software,
  protocolControlsToSave,
  setProtocolControlsToSave,
  setIsShowingControlsPopup,
  rechargeProtocolControls,
  setRechargeProtocolControls,
}) => {
  const [minimumDefault] = useState(0);
  const [maximumDefault] = useState(10);
  const [minimumColorVariability] = useState(2);
  const [maximumColorVariability] = useState(4);
  const [t] = useTranslation("global");

  const saveProtocolControls = (e) => {
    e.preventDefault();

    const protocolControlsKeys = Object.keys(protocolControlsToSave);

    const formattedProtocolControls = protocolControlsKeys.map((protocolControl) => {
      return {
        CONTROL_KEY: protocolControl,
        ...protocolControlsToSave[protocolControl],
      };
    });

    const activeLevels = formattedProtocolControls.filter((level) => level.CONTROL_KEY.includes("level_") && !level.DELETE_TIME);

    if (!activeLevels.length) return toast.error(t("controls_addOneLevelMessage"));

    const isHigherPrescription = () => {
      const maxPrescriptionDuration = 600;
      const currentPrescriptionDuration = activeLevels.reduce(
        (accumulator, currentValue) => accumulator + Number(currentValue.CONTROL_VALUE),
        0
      );
      return currentPrescriptionDuration > maxPrescriptionDuration;
    };
    if (isHigherPrescription()) return toast.error(t("controls_durationConstraintMessage"));

    UseFetch(
      "post",
      `${WEB_SERVICE_MIMS}/controls/add/${patientId}/${protocolId}/${software}`,
      new AbortController(),
      formattedProtocolControls
    )
      .then(({ data }) => {
        if (data.controlsAdded) {
          setIsShowingControlsPopup(false);
          setRechargeProtocolControls(!rechargeProtocolControls);
          toast.success(t("controls_createdControlMessage"));
        }
      })
      .catch((error) => {
        console.log("Add protocol controls: ", error);
      });
  };

  const renderHeader = () => {
    const protocolName = t(getProtocolTitle("protocols", protocolId));
    return (
      <Header>
        <Title>{protocolName}</Title>
      </Header>
    );
  };

  const getProtocolControlValue = (controlName) => {
    const protocolControl = protocolControlsToSave[controlName];
    if (!protocolControl) return 0;
    return protocolControl.CONTROL_VALUE;
  };

  const renderProtocolControlHeader = (title, controlName, controlValue) => {
    const setProtocolControl = (e) => {
      e.preventDefault();

      let protocolControlToAdd = {
        CONTROL_VALUE: controlValue ? 0 : 1,
      };

      setProtocolControlsToSave((prevState) => ({
        ...prevState,
        [controlName]: protocolControlToAdd,
      }));
    };

    return (
      <ProtocolControlHeaderDiv>
        <ControlText>{t(title)}</ControlText>
        <ToggleSwitch isActive={controlValue} setIsActive={setProtocolControl} inactiveColor="#ccc" activeColor="#39b54a" />
      </ProtocolControlHeaderDiv>
    );
  };

  const renderProtocolControl = (title, controlName, min, max, isSubcategory) => {
    const getControlValue = () => {
      const protocolControl = protocolControlsToSave[controlName];
      if (!protocolControl) return min;
      return protocolControlsToSave[controlName].CONTROL_VALUE;
    };
    const controlValue = getControlValue();

    const setProtocolControl = (e) => {
      const protocolControlToAdd = {
        CONTROL_VALUE: Number(e.target.value),
      };

      setProtocolControlsToSave((prevState) => ({
        ...prevState,
        [controlName]: protocolControlToAdd,
      }));
    };

    return (
      <ProtocolControlDiv>
        <ProtocolControlAlignerDiv paddingLeft={isSubcategory ? "20px" : "0"}>
          <ControlText>{t(title)}</ControlText>
        </ProtocolControlAlignerDiv>
        <RangeAlignerDiv paddingLeft="60px">
          <RangeSlider value={controlValue} onChange={setProtocolControl} step={1} marks min={min} max={max} />
          <ControlText margin="0 0 0 20px" opacity={controlName === "colors_variation" ? 1 : 0}>
            {parseInt(controlValue)}
          </ControlText>
        </RangeAlignerDiv>
      </ProtocolControlDiv>
    );
  };

  const renderProtocolLevels = () => {
    return (
      <ProtocolLevels
        protocolId={protocolId}
        protocolControlsToSave={protocolControlsToSave}
        setProtocolControlsToSave={setProtocolControlsToSave}
      />
    );
  };

  const renderTwisterBuddyControls = () => {
    return (
      <>
        {renderProtocolControl("twisterBuddy_barsDistance", "bars_distance", minimumDefault, maximumDefault)}
        {renderProtocolControl("twisterBuddy_distanceRightHand", "distance_target_hand_right", minimumDefault, maximumDefault)}
        {renderProtocolControl("twisterBuddy_distanceLeftHand", "distance_target_hand_left", minimumDefault, maximumDefault)}
        {renderProtocolControl("twisterBuddy_stepsThreshold", "steps_threshold", minimumDefault, maximumDefault)}
        {renderProtocolControl("twisterBuddy_distanceFootRight", "distance_target_foot_right", minimumDefault, maximumDefault)}
        {renderProtocolControl("twisterBuddy_distanceFootLeft", "distance_target_foot_left", minimumDefault, maximumDefault)}
      </>
    );
  };

  const renderCostumePartyControls = () => {
    const lateralBarControlValue = getProtocolControlValue("u_bar_enabled");
    const showCameraControlValue = getProtocolControlValue("show_camera");
    const isSubcategory = true;
    return (
      <>
        {renderProtocolControl("costumeParty_costumesSpeed", "costumes_speed", minimumDefault, maximumDefault)}
        {renderProtocolControl("costumeParty_barHeight", "bar_height", minimumDefault, maximumDefault)}
        {renderProtocolControl("costumePartyWearCostumeThreshold", "wear_costume_threshold", minimumDefault, maximumDefault)}
        {renderProtocolControlHeader("costumeParty_uBarEnabled", "u_bar_enabled", lateralBarControlValue)}
        {Boolean(lateralBarControlValue) && (
          <>{renderProtocolControl("costumeParty_uBarDistance", "u_bar_distance", minimumDefault, maximumDefault, isSubcategory)}</>
        )}
        {renderProtocolControlHeader("costumeParty_showCamera", "show_camera", showCameraControlValue)}
        {Boolean(showCameraControlValue) && (
          <>{renderProtocolControl("costumeParty_cameraRotation", "camera_rotation", minimumDefault, maximumDefault, isSubcategory)}</>
        )}
      </>
    );
  };

  const renderGentleGiantControls = () => {
    return (
      <>
        {renderProtocolControl("gentleGiant_colorsVariation", "colors_variation", minimumColorVariability, maximumColorVariability)}
        {renderProtocolControl("gentleGiant_spawnFrequency", "spawn_frequency", minimumDefault, maximumDefault)}
        {renderProtocolControl("gentleGiant_tiptoeRaise", "tiptoe_raise", minimumDefault, maximumDefault)}
        {renderProtocolControl("gentleGiant_squatDepth", "squat_depth", minimumDefault, maximumDefault)}
        {renderProtocolControl("gentleGiant_lateralMovement", "lateral_movement", minimumDefault, maximumDefault)}
      </>
    );
  };

  const renderCompensationControls = () => {
    const compensationControlValue = getProtocolControlValue("compensation_control_enabled");
    const isSubcategory = true;
    return (
      <>
        {renderProtocolControlHeader("compensationControls_enabled", "compensation_control_enabled", compensationControlValue)}
        {Boolean(compensationControlValue) && (
          <>
            {renderProtocolControl("compensationControls_torsionHip_y", "torsion_hip_y", minimumDefault, maximumDefault, isSubcategory)}
            {renderProtocolControl("compensationControls_torsionHip_z", "torsion_hip_z", minimumDefault, maximumDefault, isSubcategory)}
            {renderProtocolControl(
              "compensationControls_torsionShoulders_y",
              "torsion_shoulders_y",
              minimumDefault,
              maximumDefault,
              isSubcategory
            )}
            {renderProtocolControl(
              "compensationControls_torsionShoulders_z",
              "torsion_shoulders_z",
              minimumDefault,
              maximumDefault,
              isSubcategory
            )}
          </>
        )}
      </>
    );
  };

  const renderControlsData = () => {
    const controlsMapping = {
      [protocols.web.fullBody.TWISTER_BUDDY.id]: renderTwisterBuddyControls,
      [protocols.web.fullBody.COSTUME_PARTY.id]: renderCostumePartyControls,
      [protocols.web.fullBody.GENTLE_GIANT.id]: renderGentleGiantControls,
    };
    const protocolControlsAction = controlsMapping[protocolId] || null;

    return (
      <ControlsDiv>
        {renderProtocolLevels()}
        {protocolControlsAction()}
        {isProtocolWithCompensationControls(protocolId) && renderCompensationControls()}
      </ControlsDiv>
    );
  };

  const renderActionButtons = () => {
    const hidePopup = (e) => {
      e.preventDefault();
      setIsShowingControlsPopup(false);
    };

    return (
      <ActionButtonsDiv>
        <ActionButton onClick={saveProtocolControls} primary>
          {t("save")}
        </ActionButton>
        <ActionButton onClick={hidePopup}>{t("cancel")}</ActionButton>
      </ActionButtonsDiv>
    );
  };

  return (
    <Container>
      <>
        {renderHeader()}
        {renderControlsData()}
        {renderActionButtons()}
      </>
    </Container>
  );
};

export default ProtocolControls;
